import { useCallback, useEffect } from "react";
import {
  GitBranch,
  Copy,
  ChevronRight,
  Loader2,
  BarChart3,
  MapPin,
} from "lucide-react";
import { cn } from "../common/cn";
import { useDebateStore } from "../../store/debateStore";

const STATUS_COLORS: Record<string, string> = {
  draft: "bg-yellow-500/20 text-yellow-400",
  provisioning: "bg-blue-500/20 text-blue-400",
  running: "bg-cyan-500/20 text-cyan-400",
  finalized: "bg-emerald-500/20 text-emerald-400",
  error: "bg-red-500/20 text-red-400",
  interrupted: "bg-orange-500/20 text-orange-400",
};

const DERIVED_LABELS: Record<string, string> = {
  original: "Orijinal",
  fork: "Fork",
  duplicate: "Kopya",
};

interface Props {
  debateId: string;
  onSelectDebate?: (id: string) => void;
}

export default function VersionTreePanel({ debateId, onSelectDebate }: Props) {
  const versions = useDebateStore((s) => s.versions);
  const loading = useDebateStore((s) => s.versionsLoading);
  const error = useDebateStore((s) => s.versionsError);
  const selected = useDebateStore((s) => s.selectedForCompare);
  const loadTree = useDebateStore((s) => s.loadVersionTree);
  const selectForCompare = useDebateStore((s) => s.selectForCompare);
  const clearSelection = useDebateStore((s) => s.clearCompareSelection);
  const compare = useDebateStore((s) => s.compareDebates);

  useEffect(() => {
    loadTree(debateId);
  }, [debateId, loadTree]);

  const handleCompare = useCallback(async () => {
    const [a, b] = selected;
    if (a && b) await compare(a, b);
  }, [selected, compare]);

  const canCompare = selected[0] && selected[1];
  // Quick lookup for the "buradasınız" badge — `debateId` is the
  // currently-loaded debate, so its row in the tree gets a violet pin.
  const isCurrent = (id: string) => id === debateId;

  if (loading && versions.length === 0) {
    return (
      <div className="flex items-center justify-center py-8 text-fg-muted">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Versiyon ağacı yükleniyor…
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-400">
        {error}
      </div>
    );
  }

  if (versions.length === 0) {
    return (
      <div className="text-center text-sm text-fg-muted py-4">
        Henüz versiyon bulunmuyor.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between px-1">
        <h3 className="text-sm font-semibold text-fg-strong flex items-center gap-1.5">
          <GitBranch className="h-4 w-4 text-accent-400" />
          Versiyon Ağacı
        </h3>
        {canCompare && (
          <button
            onClick={handleCompare}
            className="flex items-center gap-1 rounded-md bg-accent-500/20 px-2.5 py-1 text-xs font-medium text-accent-300 transition hover:bg-accent-500/30"
          >
            <BarChart3 className="h-3 w-3" />
            Kıyasla
          </button>
        )}
      </div>

      {selected[0] && (
        <button
          onClick={clearSelection}
          className="text-xs text-fg-muted hover:text-fg-strong transition px-1"
        >
          Seçimi temizle
        </button>
      )}

      <div className="flex flex-col gap-1">
        {versions.map((v, idx) => {
          const isRoot = idx === 0 && v.derived_from === "original";
          const isSelected = selected[0] === v.id || selected[1] === v.id;
          const youAreHere = isCurrent(v.id);

          return (
            <div
              key={v.id}
              className={cn(
                "group relative flex items-start gap-2 rounded-lg border p-2.5 transition cursor-pointer",
                isRoot
                  ? "border-accent-500/30 bg-accent-500/5"
                  : "border-line-soft/40 bg-surface-1/50 hover:bg-surface-2/60",
                isSelected && "ring-1 ring-accent-400/50 bg-accent-500/10",
                youAreHere && "ring-2 ring-violet-400/60 bg-violet-500/[0.06]",
              )}
              onClick={() => onSelectDebate?.(v.id)}
            >
              {!isRoot && (
                <div className="absolute -left-3 top-4 h-px w-3 bg-line-soft/60" />
              )}

              <input
                type="checkbox"
                checked={isSelected}
                onChange={(e) => {
                  e.stopPropagation();
                  selectForCompare(v.id);
                }}
                className="mt-0.5 h-3.5 w-3.5 rounded border-line-soft accent-accent-500"
              />

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  {v.derived_from === "fork" ? (
                    <GitBranch className="h-3 w-3 text-fg-muted shrink-0" />
                  ) : v.derived_from === "duplicate" ? (
                    <Copy className="h-3 w-3 text-fg-muted shrink-0" />
                  ) : (
                    <ChevronRight className="h-3 w-3 text-fg-muted shrink-0" />
                  )}
                  <span className="text-xs font-medium text-fg-strong truncate">
                    {v.version_label || `v${v.version_index}`}
                  </span>
                  {youAreHere && (
                    <span className="inline-flex items-center gap-0.5 rounded-full bg-violet-500/25 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-violet-100 ring-1 ring-violet-400/40">
                      <MapPin className="h-2 w-2" aria-hidden />
                      buradasınız
                    </span>
                  )}
                  <span
                    className={cn(
                      "inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium",
                      STATUS_COLORS[v.status] ?? "bg-gray-500/20 text-gray-400",
                    )}
                  >
                    {v.status}
                  </span>
                </div>
                <div className="mt-0.5 flex items-center gap-2 text-[10px] text-fg-muted">
                  <span>{DERIVED_LABELS[v.derived_from] ?? v.derived_from}</span>
                  {v.agent_count && <span>• {v.agent_count} uzman</span>}
                  {v.created_at && (
                    <span>
                      • {new Date(v.created_at).toLocaleDateString("tr-TR")}
                    </span>
                  )}
                </div>
                {v.query && (
                  <p className="mt-1 text-[11px] text-fg-muted/70 truncate">
                    {v.query}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
